import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

/** Aviso de pagos rechazados: quién es, reintentar el cobro y pedir tarjeta nueva. */
export const AlertaImpagos = () => {
  const [items, setItems] = useState([]);
  const [ocupado, setOcupado] = useState(null);

  const load = () => api.get("/cobros/impagos").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reintentar = async (i) => {
    setOcupado(i.alumno_id);
    try {
      const { data } = await api.post(`/cobros/impagos/${i.alumno_id}/reintentar`);
      toast.success(data.estado === "paid"
        ? `Cobro realizado: ${i.alumno} vuelve a estar al día`
        : `Stripe ha dejado el cobro en estado ${data.estado}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setOcupado(null);
    }
  };

  const pedirTarjeta = async (i) => {
    setOcupado(i.alumno_id);
    try {
      await api.post("/mensajes", {
        asunto: `Pago rechazado de ${i.alumno}`,
        sede_id: i.sede_id,
        destinatarios: [{ tipo: "padre", id: i.padre_id, nombre: i.tutor || i.email }],
        cuerpo: `Hola ${i.tutor || ""}:\n\nEl cobro de la cuota de ${i.alumno} ha sido rechazado por el banco.`
          + " Para que no pierda su plaza, entra en tu área de familias y actualiza el método de pago"
          + " o vuelve a contratar su plan.\n\nSi necesitas ayuda, responde a este mensaje.\n\nGracias.",
      });
      toast.success(`Mensaje enviado a la familia de ${i.alumno}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setOcupado(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="mb-6 rounded-2xl border border-destructive/40 bg-destructive/10 p-5"
      data-testid="alerta-impagos">
      <p className="font-display font-bold text-destructive flex items-center gap-2">
        <AlertTriangle className="h-5 w-5" /> {items.length} alumno(s) con el pago rechazado
      </p>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Sus clases están en pausa hasta que se regularice el pago.
      </p>
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.alumno_id} className="rounded-xl bg-background border border-border p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid={`impago-${i.alumno_id}`}>
            <div>
              <p className="font-semibold text-sm">{i.alumno} · {i.matricula}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {i.tutor || i.email || "Sin tutor"}{i.importe ? ` · ${i.importe} €` : ""}
                {i.fallido_en ? ` · rechazado el ${i.fallido_en.slice(0, 10)}` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full font-semibold" disabled={ocupado === i.alumno_id}
                onClick={() => reintentar(i)} data-testid={`reintentar-${i.alumno_id}`}>
                {ocupado === i.alumno_id ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><RefreshCw className="h-4 w-4 mr-1.5" /> Reintentar cobro</>}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full font-semibold"
                disabled={ocupado === i.alumno_id || !i.padre_id}
                onClick={() => pedirTarjeta(i)} data-testid={`pedir-tarjeta-${i.alumno_id}`}>
                <Mail className="h-4 w-4 mr-1.5" /> Pedir tarjeta nueva
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
